// Growth helpers (Growth function, 10/01/26). Additive: nothing in api.ts changes.
// Contract: build/growth/README-contract (see the growth plan). Needs growth_1a applied first
// (calendar_adds.via/event_start/event_end, profiles.first_source/first_campaign/is_test).
//
// What lives here:
// - the calendar link contract: one add id per card, made in the browser, baked into a /f/<id> link
// - LA-correct event times (undated activities default to the coming Saturday 11:00-13:00 LA)
// - a fire-and-forget calendar_adds insert that survives the tab opening Google Calendar
// - first-touch attribution captured on the first page load
// - URL scrubbing for Sentry (capability ids never leave the browser)
import * as Sentry from '@sentry/react'
import { cleanGuests } from './calendarInvite'
import { calendarLocation } from './calLocation'
import { supabase, track, type Item } from './api'
import { APP_URL, SUPABASE_ANON_KEY, SUPABASE_URL } from '../config'
import { t } from '../copy'
import { markBrowseStale } from './browseCache'

const LA = 'America/Los_Angeles'
const HOUR = 3600e3

// ---------- storage that never throws (private windows, blocked storage) ----------
function get(key: string): string | null {
  try { return localStorage.getItem(key) } catch { return null }
}
function set(key: string, value: string) {
  try { localStorage.setItem(key, value) } catch { /* attribution is best effort */ }
}

// ---------- LA time ----------
type LaParts = { y: number; m: number; d: number; h: number; min: number; dow: number }
const DOW: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }

function laParts(at: Date, tz = LA): LaParts {
  const f = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  })
  const p: Record<string, string> = {}
  for (const part of f.formatToParts(at)) p[part.type] = part.value
  return { y: +p.year, m: +p.month, d: +p.day, h: +p.hour, min: +p.minute, dow: DOW[p.weekday] ?? 0 }
}

/** Minutes LA wall clock is ahead of UTC at this instant (negative: -420 in summer, -480 in winter). */
function laOffsetMin(at: Date, tz = LA): number {
  const p = laParts(at, tz)
  const wallAsUtc = Date.UTC(p.y, p.m - 1, p.d, p.h, p.min)
  return Math.round((wallAsUtc - Math.floor(at.getTime() / 60000) * 60000) / 60000)
}

/** The instant when the wall clock in `tz` (LA by default) reads y-m-d h:min. Device time zone never matters. */
export function laWallTime(y: number, m: number, d: number, h: number, min = 0, tz = LA): Date {
  const guess = Date.UTC(y, m - 1, d, h, min)
  let at = guess - laOffsetMin(new Date(guess), tz) * 60000
  const off2 = laOffsetMin(new Date(at), tz)
  at = guess - off2 * 60000
  return new Date(at)
}

/** The coming Saturday, 11:00-13:00 LA time. On a Saturday it means next Saturday (same rule as before). */
export function comingSaturdayLA(now = new Date(), tz = LA): { start: Date; end: Date } {
  const p = laParts(now, tz)
  const add = (6 - p.dow + 7) % 7 || 7
  const day = new Date(Date.UTC(p.y, p.m - 1, p.d + add))
  const start = laWallTime(day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate(), 11, 0, tz)
  return { start, end: new Date(start.getTime() + 2 * HOUR) }
}

/** The coming Tuesday, 19:00-21:00 LA time (today if it's Tuesday before 7pm). The weeknight default slot. */
export function nextTuesdayEveningLA(now = new Date()): { start: Date; end: Date } {
  const p = laParts(now)
  let add = (2 - p.dow + 7) % 7
  if (add === 0 && p.h >= 19) add = 7
  const day = new Date(Date.UTC(p.y, p.m - 1, p.d + add))
  const start = laWallTime(day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate(), 19)
  return { start, end: new Date(start.getTime() + 2 * HOUR) }
}

/** The next of the chosen weekdays (0=Sun..6=Sat), 11:00-13:00 LA; today counts until 11am. */
export function nextChosenDayLA(days: number[], now = new Date()): { start: Date; end: Date } | null {
  if (!days.length) return null
  const p = laParts(now)
  for (let add = 0; add < 8; add++) {
    if (!days.includes((p.dow + add) % 7) || (add === 0 && p.h >= 11)) continue
    const day = new Date(Date.UTC(p.y, p.m - 1, p.d + add))
    const start = laWallTime(day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate(), 11)
    return { start, end: new Date(start.getTime() + 2 * HOUR) }
  }
  return null
}

/** The person's week window as calendar code needs it: the old string form, or the week context with chosen days. */
export type Slot = string | { when?: string | null; days?: number[] | null } | null

/**
 * When the calendar event happens.
 * - Dated event: its own start; end = end_at, or start + 2h.
 * - Multi-day run (end - start > 12h, e.g. an exhibit): the coming Saturday 11-13 LA if that falls
 *   inside the run, else start + 2h. A calendar block never lasts weeks.
 * - Undated activity: the coming Saturday 11-13 LA.
 */
export function eventTimes(item: Item, now = new Date(), slot?: Slot, tz = LA): { start: Date; end: Date } {
  const when = typeof slot === 'string' ? slot : (slot?.when ?? null)
  const days = typeof slot === 'object' && slot?.days?.length ? slot.days : null
  // undated: the slot that matches the person's week window
  // (chosen days -> the next of them 11am; weeknight -> Tue 7pm; weekend/any -> Sat 11am)
  const sat = (days && nextChosenDayLA(days, now)) || (when === 'weeknight' ? nextTuesdayEveningLA(now) : comingSaturdayLA(now, tz))
  if (!item.start_at) return sat
  const start = new Date(item.start_at)
  const end = item.end_at ? new Date(item.end_at) : null
  if (end && end.getTime() > start.getTime() && end.getTime() - start.getTime() <= 12 * HOUR) return { start, end }
  if (end && end.getTime() - start.getTime() > 12 * HOUR) {
    if (sat.start.getTime() >= start.getTime() && sat.end.getTime() <= end.getTime()) return sat
    const from = start.getTime() > now.getTime() ? start : now
    return { start: from, end: new Date(Math.min(from.getTime() + 2 * HOUR, end.getTime())) }
  }
  return { start, end: new Date(start.getTime() + 2 * HOUR) }
}

// ---------- the calendar link contract ----------
/** One id per card per page session. Keep it in a ref/Map keyed by item id; never regenerate on re-render. */
export function newAddId(): string {
  return crypto.randomUUID()
}

/** Frozen path format (DATA-MAP §8): /f/<uuid>. The host never changes. */
export function feedbackLink(addId: string): string {
  return `${APP_URL}/f/${addId}`
}

export function utcStamp(d: Date) {
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
}

function details(item: Item, addId: string) {
  return [
    item.one_line,
    item.price_text ? `Price: ${item.price_text}` : '',
    `Details: ${item.url}`,
    t('calendar.event.feedback_line', { FEEDBACK_LINK: feedbackLink(addId) }),
    'Picked for you by Magellan',
  ].filter(Boolean).join('\n')
}

function place(item: Item) {
  return calendarLocation(item) // lib/calLocation.ts: venue + street address, else venue + city
}

const ymd = (p: { y: number; m: number; d: number }) => `${p.y}${String(p.m).padStart(2, '0')}${String(p.d).padStart(2, '0')}`
/**
 * An all-day thing (review M1): it starts at midnight in its city and has no end, or ends at a midnight too (venue
 * tours, all-day listings). Returns the first day and the day after the last, as calendar DATE values (YYYYMMDD).
 */
export function allDayDates(item: Item, tz = LA): { from: string; to: string } | null {
  if (!item.start_at) return null
  const s = laParts(new Date(item.start_at), tz)
  if (s.h !== 0 || s.min !== 0) return null
  let last = s
  if (item.end_at) {
    const e = laParts(new Date(item.end_at), tz)
    const endMs = new Date(item.end_at).getTime()
    if (endMs > new Date(item.start_at).getTime()) {
      if (e.h !== 0 || e.min !== 0) return null
      // an end at midnight closes the day before it
      const prev = new Date(Date.UTC(e.y, e.m - 1, e.d - 1))
      last = { ...e, y: prev.getUTCFullYear(), m: prev.getUTCMonth() + 1, d: prev.getUTCDate() }
      if (ymd(last) < ymd(s)) last = s
    }
  }
  const next = new Date(Date.UTC(last.y, last.m - 1, last.d + 1))
  return { from: ymd(s), to: ymd({ y: next.getUTCFullYear(), m: next.getUTCMonth() + 1, d: next.getUTCDate() }) }
}

/** Google Calendar template link. Render it as <a href target="_blank" rel="noopener"> so the app tab stays open.
 *  `tz`: the activity's city (review M2); undated activities default to Saturday 11:00 there. */
export function googleCalendarUrl(item: Item, addId: string, now = new Date(), when?: Slot, tz = LA, guests: string[] = []): string {
  const all = allDayDates(item, tz)
  const { start, end } = eventTimes(item, now, when, tz)
  const p = new URLSearchParams({
    action: 'TEMPLATE', text: item.title, dates: all ? `${all.from}/${all.to}` : `${utcStamp(start)}/${utcStamp(end)}`,
    details: details(item, addId), location: place(item), ctz: tz,
  })
  // pin 596: an itinerary stop with a group carries its guests (lib/calendarInvite.ts cleans the list)
  const g = cleanGuests(guests)
  if (g.length) p.set('add', g.join(','))
  return `https://calendar.google.com/calendar/render?${p.toString()}`
}

/** RFC 5545 folding: content lines at most 75 octets, continued after CRLF + space; never splits a character. */
export function fold(line: string): string {
  const enc = new TextEncoder()
  const out: string[] = []
  let cur = ''
  let bytes = 0
  for (const ch of line) {
    const n = enc.encode(ch).length
    const limit = out.length ? 74 : 75 // a continuation line spends one octet on its leading space
    if (bytes + n > limit) {
      out.push(cur)
      cur = ''
      bytes = 0
    }
    cur += ch
    bytes += n
  }
  out.push(cur)
  return out.join('\r\n ')
}

/** .ics file for the same add id (blob tonight; a served text/calendar URL replaces it by 10/03). */
export function icsBlob(item: Item, addId: string, now = new Date(), when?: Slot, tz = LA, guests: string[] = []): Blob {
  const all = allDayDates(item, tz)
  const { start, end } = eventTimes(item, now, when, tz)
  const esc = (s: string) => s.replace(/\r/g, '').replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n')
  const ics = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Magellan//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH', 'BEGIN:VEVENT',
    `UID:${addId}@magellan`, `DTSTAMP:${utcStamp(now)}`,
    ...(all ? [`DTSTART;VALUE=DATE:${all.from}`, `DTEND;VALUE=DATE:${all.to}`] : [`DTSTART:${utcStamp(start)}`, `DTEND:${utcStamp(end)}`]),
    // the source link travels in DESCRIPTION (review L2: no unescaped URL line)
    `SUMMARY:${esc(item.title)}`, `DESCRIPTION:${esc(details(item, addId))}`, `LOCATION:${esc(place(item))}`,
    ...cleanGuests(guests).map((g) => `ATTENDEE;CN="${g}";ROLE=REQ-PARTICIPANT;RSVP=TRUE:mailto:${g}`), // pin 596
    'END:VEVENT', 'END:VCALENDAR',
  ].map(fold).join('\r\n')
  return new Blob([ics], { type: 'text/calendar;charset=utf-8' })
}

// ---------- recording the add ----------
let priorAdds: number | null = null
const localAdds = new Set<string>()

/** Adds this person had before this page session; null until primeAddCount() has returned. */
export function priorAddCount(): number | null {
  return priorAdds
}

/** Call once when the picks page loads, alongside getPicks(). Lets "first add" be known without a round trip. */
export async function primeAddCount(): Promise<void> {
  const { data } = await supabase.auth.getSession()
  const uid = data.session?.user.id
  if (!uid) return
  const { count, error } = await supabase.from('calendar_adds').select('id', { count: 'exact', head: true }).eq('user_id', uid)
  if (!error) priorAdds = count ?? 0
}

/**
 * Record a calendar add. Synchronous answer, nothing awaited before the link opens.
 * The insert uses the browser-made id, ignores duplicates (double taps, Google then .ics on the same card)
 * and is sent with keepalive so it survives the page going to the background.
 */
export function recordAdd(
  item: Item, method: 'google' | 'ics', addId: string, now = new Date(), when?: Slot,
  /** A night: the event spans the whole night and the add points at the saved night (anchor item). */
  night?: { start: Date; end: Date; nightId: string },
  /** false when the caller awaits `sent` before leaving (DetailCalendar): Chromium reports a finished keepalive POST as
   *  net::ERR_ABORTED, which read as a lost add in the journey and blind-test logs (B-056). */
  keepalive = true,
): { first: boolean; sent: Promise<void> } {
  const isNew = !localAdds.has(addId)
  const first = isNew && priorAdds === 0 && localAdds.size === 0
  localAdds.add(addId)
  if (!isNew) return { first: false, sent: Promise.resolve() }
  const { start, end } = night ?? eventTimes(item, now, when)
  // `sent` settles once the insert has answered (B-074: a .ics download started first left it ERR_ABORTED in testers' logs)
  const sent = supabase.auth.getSession().then(({ data }) => {
    const s = data.session
    if (!s) return
    const row = {
      id: addId, user_id: s.user.id, item_id: item.id, method, via: 'picks',
      event_start: start.toISOString(), event_end: end.toISOString(),
    }
    const post = (body: Record<string, unknown>) => fetch(`${SUPABASE_URL}/rest/v1/calendar_adds`, {
      method: 'POST',
      keepalive,
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${s.access_token}`,
        'Content-Type': 'application/json',
        Prefer: 'resolution=ignore-duplicates,return=minimal',
      },
      body: JSON.stringify(body),
    })
    // A database without calendar_adds.night_id yet answers 400: the add is still recorded, without the night.
    const saved = post(night ? { ...row, night_id: night.nightId } : row)
      .then((r) => (r.status === 400 && night ? post(row) : r))
      // read the (empty) body to the end: a 201 whose body was never read is cut off by the .ics download right after it and
      // shows as net::ERR_ABORTED in logs although the row landed (traced 10/10: RESP 201, then FAIL, then the download)
      .then(async (r) => { await r.text().catch(() => ''); if (!r.ok) Sentry.captureMessage(`calendar_adds insert ${r.status}`) })
      .catch((e) => Sentry.captureException(e))
    track(first ? 'first_calendar_add' : 'calendar_add', {
      item_id: item.id, method, ...(night ? { night: true } : {}), kind: item.kind, dated: Boolean(item.start_at), iykyk: item.iykyk_score, inapp: inAppBrowser(),
    })
    return saved.then(() => { markBrowseStale() })
  }).catch(() => {})
  return { first, sent }
}

// ---------- environment ----------
/** iPhone, iPad (iPadOS reports a Mac) and Mac: the built-in calendar opens .ics files, so it leads. */
export function applePlatform(): boolean {
  if (typeof navigator === 'undefined') return false
  return /iPhone|iPad|iPod|Macintosh|Mac OS X/i.test(navigator.userAgent) && !/Android/i.test(navigator.userAgent)
}

/** Instagram, Facebook, TikTok, LinkedIn, X and Snapchat webviews: Google sign-in is blocked there. */
export function inAppBrowser(): boolean {
  if (typeof navigator === 'undefined') return false
  return /Instagram|FBAN|FBAV|FB_IAB|TikTok|musical_ly|Bytedance|LinkedInApp|Twitter|Snapchat|Line\//i.test(navigator.userAgent)
}

// ---------- first-touch attribution ----------
type Source = 'direct' | 'campaign' | 'partner' | 'share' | 'finder'
const ATTR_KEY = 'mg_attr'
const TEST_KEY = 'mg_test'
/** Flip to true once growth_1b (log_landing) is applied. */
const LANDING_RPC = false

/** Call once in main.tsx before the first render, while ?c= is still in the URL. */
export function captureAttribution(loc: { pathname: string; search: string } = window.location): void {
  const raw = (new URLSearchParams(loc.search).get('c') ?? '').trim().toLowerCase()
  if (raw === 'test') set(TEST_KEY, '1') // always wins, even on a device that already has attribution
  const path = loc.pathname
  if (path.startsWith('/f/') || path.startsWith('/w/')) return // a returning person, never a new source
  if (get(ATTR_KEY)) return
  let source: Source = path.startsWith('/join/') ? 'partner' : path.startsWith('/e/') ? 'share' : 'direct'
  let campaign: string | null = null
  if (raw && raw !== 'test') {
    campaign = /^[a-z0-9-]{1,32}$/.test(raw) ? raw : 'other'
    if (source === 'direct') source = campaign.startsWith('f-') ? 'finder' : 'campaign'
  }
  set(ATTR_KEY, JSON.stringify({ source, campaign }))
  if (LANDING_RPC) void supabase.rpc('log_landing', { tag: campaign ?? source }).then(() => undefined)
}

/** Spread into every saveProfile() call. The database keeps the first value, so repeats are harmless. */
export function attributionFields(): { first_source?: Source; first_campaign?: string | null; is_test?: boolean } {
  const out: { first_source?: Source; first_campaign?: string | null; is_test?: boolean } = {}
  try {
    const a = JSON.parse(get(ATTR_KEY) ?? 'null') as { source: Source; campaign: string | null } | null
    if (a?.source) {
      out.first_source = a.source
      out.first_campaign = a.campaign
    }
  } catch { /* ignore a corrupted value */ }
  if (get(TEST_KEY) === '1') out.is_test = true
  return out
}

// ---------- Sentry scrub ----------
/** Replace capability ids in any URL-ish string: /f/<uuid>, /e/<uuid>, /join/<code>, /w/<token>, ?s=<id>. */
export function scrubPath(s: string): string {
  return s
    .replace(/\/(f|e)\/[0-9a-f-]{36}/gi, '/$1/:id')
    .replace(/\/join\/[A-Za-z0-9-]+/g, '/join/:code')
    .replace(/\/w\/[0-9a-f]{16,64}/gi, '/w/:token')
    .replace(/([?&])s=[^&#"\s]*/g, '$1s=:id')
}

/** For beforeSend, beforeSendTransaction and beforeBreadcrumb: scrub every string in the payload. */
export function scrubDeep<T>(payload: T): T {
  try {
    return JSON.parse(scrubPath(JSON.stringify(payload))) as T
  } catch {
    return payload
  }
}
